import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import DateInput from '@/components/ui/custom/dataInput/DateInput';
import EnumInput from '@/components/ui/custom/dataInput/EnumInput';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import { HackathonStage, StageTypeEnum } from '@/models/Hackathon';
import { setEndDate } from '@/utils/datetimes';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type HackathonStagesField = Omit<HackathonStage, 'id' | 'hackathonId'>;

type SubmitFn = (stages: HackathonStagesField) => void;

type HackathonStageFormProps = {
  hackathonStage: HackathonStage;
  onDelete: (stageId: number) => void;
  isDeleting?: boolean;
  onSubmit: SubmitFn;
  isLoading?: boolean;
  hasChanges?: boolean;
  setHasChanges?: (hasChanges: boolean) => void;
};

const HackathonStageForm = ({
  hackathonStage,
  onDelete,
  isDeleting,
  isLoading,
  hasChanges,
  setHasChanges,
  onSubmit,
}: HackathonStageFormProps) => {
  const [modal, setModal] = useState(false);
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const [tStages] = useTranslation('translation', {
    keyPrefix: 'hackathon.stages',
  });
  const [tForm] = useTranslation('translation', {
    keyPrefix: 'hackathon.form.hackathonStages',
  });
  const [tFormHackathon] = useTranslation('translation', {
    keyPrefix: 'hackathon.form.hackathon',
  });
  const defaultValues = useMemo(
    () => ({
      type: hackathonStage.type,
      endDate: hackathonStage.endDate,
      startDate: hackathonStage.startDate,
    }),
    [hackathonStage],
  );
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<HackathonStagesField>({
    defaultValues,
  });

  const { field: startDateField } = useController({
    control,
    name: `startDate`,
    rules: {
      required: tFormHackathon('required'),
    },
  });
  const { field: endDateField } = useController({
    control,
    name: `endDate`,
    rules: {
      required: tFormHackathon('required'),
    },
  });
  const { field: typeField } = useController({
    control,
    name: `type`,
    rules: {
      required: tFormHackathon('required'),
    },
  });

  const updateDate = (date: string, key: keyof HackathonStage) => {
    if (key === 'endDate') date = setEndDate(date);
    return date;
  };

  const onSubmitClean: SubmitFn = fields => onSubmit(fields);

  useEffect(() => {
    const subscription = watch(fields => {
      setHasChanges?.(!isEqual(fields, defaultValues));
    });
    return () => subscription.unsubscribe();
  }, [watch, setHasChanges, defaultValues]);
  return (
    <form
      onSubmit={handleSubmit(onSubmitClean)}
      className="flex flex-col gap-2.5 max-w-[500px]"
    >
      <EnumInput
        enumModel={StageTypeEnum}
        displayName={t => tStages(t)}
        selected={typeField.value}
        onSelect={typeField.onChange}
        errorLabelText={errors.type?.message}
        type="string"
        label={tForm('type.label')}
        placeholder={tForm('type.placeholder')}
      />
      <DateInput
        label={t('form.startDate.title')}
        testId="startDate"
        value={startDateField.value}
        errorLabelText={errors.startDate?.message}
        className={{
          base: 'w-full gap-1',
          input: 'font-500',
          reference: 'w-full flex',
        }}
        onDateChange={date =>
          startDateField.onChange(updateDate(date, 'startDate'))
        }
      />
      <DateInput
        errorLabelText={errors.endDate?.message}
        label={t('form.endDate.title')}
        testId="endDate"
        value={endDateField.value}
        className={{
          base: 'w-full gap-1',
          input: 'font-500',
          reference: 'w-full flex',
        }}
        onDateChange={date =>
          endDateField.onChange(updateDate(date, 'endDate'))
        }
      />

      <SaveCancelGroup
        cancel={{
          className: 'bg-error-content text-error !text-14',
          text: t('delete'),
          type: 'button',
          loading: isDeleting,
          onClick: () => setModal(true),
        }}
        save={{
          disabled: !hasChanges,
          loading: isLoading,
        }}
      />
      <div className="h-px bg-primary w-full" />
      <ModalDisable
        onClickConfirm={() => onDelete(hackathonStage.id)}
        visible={modal}
        isRequesting={isDeleting}
        translationString="modalDisableHackatonStage"
        onClickCancel={() => setModal(false)}
      />
    </form>
  );
};

export default HackathonStageForm;
